/**
 * ServerTable — the register every list page in Connect is built on.
 *
 * There is exactly one of these. The reference implementation's version was
 * ~1,700 lines on MUI; this is the same behaviour and the same wire protocol,
 * rendered on Tailwind primitives.
 *
 * What it does, and the reasoning where it is not obvious:
 *
 * - **Server-side everything.** Paging, sorting, filtering and search happen in
 *   the database. A table that filters client-side is correct until the dataset
 *   outgrows one page, and then silently wrong.
 * - **Sort and filters live in the URL; the arrangement lives in storage.** A
 *   narrowed view is a link somebody can send. A column arrangement is personal
 *   and would be noise in a shared link.
 * - **`keepPreviousData`** while a page loads, so paging does not blank the
 *   table and bounce the scroll position.
 * - **Selection spans pages** via "select all matching", because a bulk action
 *   over 1,200 rows is the reason bulk actions exist — and the flag is sent to
 *   the server rather than 1,200 ids.
 * - **Row detail, inline edit, row actions, bulk actions, facet counts, column
 *   resize, pinning, reorder, visibility** — all present, because a register
 *   that has most of them sends people to a spreadsheet for the rest.
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Columns3,
  Filter,
  FilterX,
  MoreHorizontal,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react';

import { appConfig } from '../../config/app-config';
import { cn } from '../../lib/cn';
import { EMPTY } from '../../lib/empty';
import { formatNumber } from '../../lib/format';
import { useDebounced, useFillHeight, useIsMobile } from '../../lib/hooks';
import { ApiError } from '../../lib/api-client';
import { Button, ToolbarButton } from '../ui/button';
import { Checkbox } from '../ui/input';
import { Pager } from '../ui/layout';
import { Confirm, DropdownMenu, Popover, type MenuItem } from '../ui/overlays';
import { Badge } from '../ui/surfaces';
import { ErrorState, EmptyState, NoMatches, TableSkeleton } from '../states';
import { ColumnFilter } from './column-filter';
import { ColumnsPanel } from './columns-panel';
import {
  filterChipLabel,
  isActiveFilter,
  type FacetOption,
  type FilterValue,
  type ServerColumn,
  type ServerListRequest,
  type ServerListResult,
  type SortCriterion,
  type SortDir,
} from './types';
import { cycleSort, showsLayoutControl, sortMapOf, useTableState } from './use-table-state';
import { ScrollArea } from '../ui/scroll-area';

/** One label/value pair in an expanded row. */
export interface RowDetailField {
  label: string;
  value: ReactNode;
}

export interface ServerTableProps<T> {
  columns: readonly ServerColumn<T>[];
  /** Fetches one page. Given the whole request so nothing is inferred. */
  fetchRows: (request: ServerListRequest) => Promise<ServerListResult<T>>;
  /** Stable identity per row, for selection and React keys. */
  rowKey: (row: T) => string;
  /** React Query cache key. Include anything outside the request that changes the data. */
  queryKey: readonly unknown[];
  /** Namespace for the saved arrangement. Unique per register. */
  stateKey: string;

  search?: boolean;
  searchPlaceholder?: string;

  /** Facet counts for a select filter, cross-filtered by the other filters. */
  getFacets?: (field: string, request: ServerListRequest) => Promise<FacetOption[]>;

  onView?: (row: T) => void;
  /**
   * Which column becomes the link to the record.
   *
   * Named explicitly rather than defaulting to the first visible column,
   * because that shifts the moment somebody reorders the table and the link
   * lands on a date.
   */
  viewField?: string;
  onEdit?: (row: T) => void;
  /**
   * Deleting one row. Always behind a confirmation — see `deleteLabel`.
   */
  onDelete?: (row: T) => Promise<void> | void;
  /**
   * How the record is named in the delete confirmation.
   *
   * "Delete Acme Industries?" is answerable; "Are you sure?" is not, because it
   * does not say what is about to be deleted. Defaults to the link column's
   * value, which is the name in almost every register.
   */
  deleteLabel?: (row: T) => string;
  /** Extra per-row actions, appended to the row menu. */
  rowActions?: (row: T) => MenuItem[];

  /** Enables double-click-to-edit on columns marked `editable`. */
  onCellEdited?: (row: T, field: string, value: string) => Promise<void> | void;

  /**
   * Expandable row detail as labelled fields.
   *
   * Preferred over {@link renderRowDetail}: the table owns the layout, so every
   * expanded row in the product reads the same way. Use it for what a row
   * cannot show — the columns a narrow table drops — not a repeat of what is
   * already on screen.
   */
  rowDetailFields?: (row: T) => readonly RowDetailField[];
  /** Escape hatch for a detail panel that is not a field grid. Wins over the above. */
  renderRowDetail?: (row: T) => ReactNode;

  selectable?: boolean;
  /** The bar shown while rows are selected. */
  bulkActions?: (context: {
    ids: string[];
    allMatching: boolean;
    total: number;
    clear: () => void;
    /**
     * The search, filters and sort behind the selection.
     *
     * `ids` holds only the rows ticked on screen, so with `allMatching` an
     * action that needs the actual records has to ask the endpoint for them
     * again — under exactly this request, or it acts on a different set than
     * the one the user selected.
     */
    request: ServerListRequest;
  }) => ReactNode;
  /** Adds a Delete-selected button with a confirmation. */
  onBulkDelete?: (ids: string[], allMatching: boolean) => Promise<void> | void;

  /** Extra toolbar controls, before the built-in ones. */
  toolbarActions?: ReactNode;

  /** A row to flash and scroll to — a record just created. */
  recentKey?: string | null;

  /** Who decides whether the arrangement persists. See `showsLayoutControl`. */
  layoutSaving?: 'control' | 'always';

  /** Shown when the register holds nothing at all — not when a filter matched nothing. */
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;

  /** Fills the viewport with an internal scroll. Off for a table inside a card. */
  fillHeight?: boolean;
  defaultPageSize?: number;
  className?: string;
}

/** URL keys. Prefixed so two tables on one page cannot collide. */
function urlKeys(stateKey: string) {
  const prefix = stateKey.replace(/[^a-z0-9]/gi, '');
  return {
    q: `${prefix}_q`,
    sort: `${prefix}_sort`,
    filters: `${prefix}_f`,
    page: `${prefix}_p`,
  };
}

export function ServerTable<T>({
  columns,
  fetchRows,
  rowKey,
  queryKey,
  stateKey,
  search = true,
  searchPlaceholder = 'Search',
  getFacets,
  onView,
  viewField,
  onEdit,
  onDelete,
  deleteLabel,
  rowActions,
  onCellEdited,
  rowDetailFields,
  renderRowDetail,
  selectable = false,
  bulkActions,
  onBulkDelete,
  toolbarActions,
  recentKey,
  layoutSaving,
  emptyTitle,
  emptyDescription,
  emptyAction,
  fillHeight = true,
  defaultPageSize = 25,
  className,
}: ServerTableProps<T>) {
  const keys = useMemo(() => urlKeys(stateKey), [stateKey]);
  const [params, setParams] = useSearchParams();
  const isMobile = useIsMobile();
  const { layout, update, saveNow, revert, reset, unsaved } = useTableState(stateKey, {
    forceAutoSave: !showsLayoutControl(appConfig().layoutSaving, layoutSaving),
    defaultPageSize,
  });

  // ── the view state, read from the URL ─────────────────────
  const page = Math.max(1, Number(params.get(keys.page) ?? '1') || 1);
  const urlSearch = params.get(keys.q) ?? '';
  const [searchDraft, setSearchDraft] = useState(urlSearch);
  const debouncedSearch = useDebounced(searchDraft, 300);

  const sort = useMemo<SortCriterion[]>(() => {
    const raw = params.get(keys.sort);
    if (!raw) return [];
    return raw
      .split(',')
      .map((token) => {
        const [field, dir] = token.split(':');
        if (!field) return null;
        return { field, dir: (dir === 'desc' ? 'desc' : 'asc') as SortDir };
      })
      .filter((criterion): criterion is SortCriterion => criterion !== null);
  }, [params, keys.sort]);

  const filters = useMemo<Record<string, FilterValue>>(() => {
    const raw = params.get(keys.filters);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(decodeURIComponent(raw)) as Record<string, FilterValue>;
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      // A truncated or hand-edited URL must not break the page — it degrades to
      // no filters, which is recoverable and obvious.
      return {};
    }
  }, [params, keys.filters]);

  const [filtersOn, setFiltersOn] = useState(true);
  const [disabledFilters, setDisabledFilters] = useState<string[]>([]);

  /** Push view state into the URL, replacing rather than stacking history. */
  const setUrl = useCallback(
    (patch: Partial<{ q: string; sort: SortCriterion[]; filters: Record<string, FilterValue>; page: number }>) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (patch.q !== undefined) {
            if (patch.q) next.set(keys.q, patch.q);
            else next.delete(keys.q);
          }
          if (patch.sort !== undefined) {
            const encoded = patch.sort.map((c) => `${c.field}:${c.dir}`).join(',');
            if (encoded) next.set(keys.sort, encoded);
            else next.delete(keys.sort);
          }
          if (patch.filters !== undefined) {
            const active = Object.fromEntries(
              Object.entries(patch.filters).filter(([, value]) => isActiveFilter(value)),
            );
            if (Object.keys(active).length > 0) {
              next.set(keys.filters, encodeURIComponent(JSON.stringify(active)));
            } else {
              next.delete(keys.filters);
            }
          }
          // Any change to what is being looked at resets to page one. Staying
          // on page four of a narrower result set shows an empty table, which
          // reads as "no matches" when there are plenty on page one.
          const nextPage = patch.page ?? (patch.q !== undefined || patch.sort !== undefined || patch.filters !== undefined ? 1 : page);
          if (nextPage > 1) next.set(keys.page, String(nextPage));
          else next.delete(keys.page);
          return next;
        },
        { replace: true },
      );
    },
    [setParams, keys, page],
  );

  // The debounced search reaches the URL once typing settles.
  useEffect(() => {
    if (debouncedSearch !== urlSearch) setUrl({ q: debouncedSearch });
  }, [debouncedSearch, urlSearch, setUrl]);

  // ── the effective request ─────────────────────────────────
  const effectiveFilters = useMemo(() => {
    if (!filtersOn) return {};
    return Object.fromEntries(
      Object.entries(filters).filter(([field]) => !disabledFilters.includes(field)),
    );
  }, [filters, filtersOn, disabledFilters]);

  const request = useMemo<ServerListRequest>(
    () => ({
      q: debouncedSearch || undefined,
      filters: effectiveFilters,
      sort,
      page,
      pageSize: layout.pageSize,
    }),
    [debouncedSearch, effectiveFilters, sort, page, layout.pageSize],
  );

  const query = useQuery({
    queryKey: [...queryKey, request],
    queryFn: () => fetchRows(request),
    // The previous page stays on screen while the next loads, so paging does
    // not blank the table and lose the scroll position.
    placeholderData: keepPreviousData,
  });

  // `EMPTY`, not `[]`: this value reaches two dependency lists below, and a
  // fresh literal each render makes them fire while the page is still loading.
  const rows = query.data?.rows ?? (EMPTY as readonly T[]);
  const total = query.data?.total ?? 0;

  // ── column arrangement ────────────────────────────────────
  const ordered = useMemo(() => {
    const byField = new Map(columns.map((column) => [column.field, column]));
    const seen = new Set<string>();
    const result: ServerColumn<T>[] = [];
    // Saved order first, then anything the saved order did not know about —
    // so a column added in a new release appears rather than disappearing for
    // everybody with a stored arrangement.
    for (const field of layout.order) {
      const column = byField.get(field);
      if (column) {
        result.push(column);
        seen.add(field);
      }
    }
    for (const column of columns) {
      if (!seen.has(column.field)) result.push(column);
    }
    return result;
  }, [columns, layout.order]);

  const hiddenFields = useMemo(() => {
    // A column hidden by default counts as hidden until the arrangement says
    // otherwise; once there is a stored arrangement, it is authoritative.
    if (layout.order.length === 0 && layout.hidden.length === 0) {
      return columns.filter((column) => column.hiddenByDefault).map((column) => column.field);
    }
    return layout.hidden;
  }, [columns, layout.order.length, layout.hidden]);

  const visible = useMemo(
    () => ordered.filter((column) => !hiddenFields.includes(column.field)),
    [ordered, hiddenFields],
  );

  const pinnedLeft = useMemo(
    () => visible.filter((column) => layout.pins[column.field] === 'left'),
    [visible, layout.pins],
  );
  const unpinned = useMemo(
    () => visible.filter((column) => !layout.pins[column.field]),
    [visible, layout.pins],
  );
  const displayed = useMemo(() => [...pinnedLeft, ...unpinned], [pinnedLeft, unpinned]);

  const linkField = viewField ?? displayed[0]?.field;

  // ── selection ─────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);

  // Any change to what is being looked at clears the selection. Keeping it
  // across a filter change means a bulk action over rows the user can no
  // longer see — which is the worst possible time to be confident.
  useEffect(() => {
    setSelected(new Set());
    setAllMatching(false);
  }, [request.filters, request.q, request.sort, request.page]);

  const pageKeys = useMemo(() => rows.map(rowKey), [rows, rowKey]);
  const allOnPageSelected = pageKeys.length > 0 && pageKeys.every((key) => selected.has(key));
  const someOnPageSelected = pageKeys.some((key) => selected.has(key));

  const toggleRow = (key: string) => {
    setAllMatching(false);
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const togglePage = () => {
    setAllMatching(false);
    setSelected((previous) => {
      const next = new Set(previous);
      if (allOnPageSelected) pageKeys.forEach((key) => next.delete(key));
      else pageKeys.forEach((key) => next.add(key));
      return next;
    });
  };

  const clearSelection = () => {
    setSelected(new Set());
    setAllMatching(false);
  };

  const selectionCount = allMatching ? total : selected.size;

  // ── expansion ─────────────────────────────────────────────
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const expandable = Boolean(rowDetailFields || renderRowDetail);

  const toggleExpanded = (key: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ── inline editing ────────────────────────────────────────
  const [editing, setEditing] = useState<{ key: string; field: string; value: string } | null>(null);

  // ── the delete confirmation ───────────────────────────────
  // Held here rather than in each page, so no register can ship a delete that
  // fires straight from a menu click.
  const [pendingDelete, setPendingDelete] = useState<T | null>(null);

  const commitEdit = async (row: T, field: string, value: string) => {
    setEditing(null);
    if (!onCellEdited) return;
    await onCellEdited(row, field, value);
    await query.refetch();
  };

  // ── filter and column popovers ────────────────────────────
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const filterAnchors = useRef<Record<string, HTMLButtonElement | null>>({});
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsAnchor = useRef<HTMLButtonElement>(null);

  const facetQuery = useQuery({
    queryKey: [...queryKey, 'facets', openFilter, request.filters, request.q],
    queryFn: () => getFacets!(openFilter!, request),
    // Loaded when the popover opens, never before: facet counts for every
    // column on every render would be a query per column per keystroke.
    enabled: Boolean(openFilter && getFacets),
  });

  // ── the recently-created row ──────────────────────────────
  const recentRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (recentKey && recentRef.current) {
      recentRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [recentKey, rows]);

  // ── resizing ──────────────────────────────────────────────
  const resizing = useRef<{ field: string; startX: number; startWidth: number } | null>(null);

  const startResize = (field: string, event: React.PointerEvent) => {
    const header = (event.currentTarget as HTMLElement).closest('th');
    resizing.current = {
      field,
      startX: event.clientX,
      startWidth: header?.getBoundingClientRect().width ?? 160,
    };
    // Captured on the window so a fast drag that leaves the handle keeps
    // resizing — a pointer-move bound to the handle stops the moment the
    // cursor outpaces it.
    const onMove = (moveEvent: PointerEvent) => {
      const state = resizing.current;
      if (!state) return;
      const width = Math.max(64, state.startWidth + (moveEvent.clientX - state.startX));
      update({ widths: { ...layout.widths, [state.field]: Math.round(width) } });
    };
    const onUp = () => {
      resizing.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ── layout ────────────────────────────────────────────────
  const { ref: fillRef, height } = useFillHeight();
  const activeFilterFields = Object.keys(filters).filter((field) => isActiveFilter(filters[field]));
  const hasNarrowing = activeFilterFields.length > 0 || Boolean(debouncedSearch);
  const showsControl = showsLayoutControl(appConfig().layoutSaving, layoutSaving);

  const clearAll = () => {
    setSearchDraft('');
    setDisabledFilters([]);
    setFiltersOn(true);
    setUrl({ q: '', filters: {} });
  };

  // ── render ────────────────────────────────────────────────
  const body = (() => {
    if (query.isError) {
      const error = query.error;
      return (
        <ErrorState
          message={error instanceof ApiError ? error.message : undefined}
          requestId={error instanceof ApiError ? error.requestId : undefined}
          onRetry={() => void query.refetch()}
        />
      );
    }
    if (query.isLoading) {
      return <TableSkeleton columns={Math.min(displayed.length || 5, 7)} />;
    }
    if (rows.length === 0) {
      // The two empty cases are genuinely different, and one message for both
      // is wrong half the time.
      return hasNarrowing ? (
        <NoMatches onClear={clearAll} />
      ) : (
        <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
      );
    }
    return null;
  })();

  return (
    <div
      ref={fillHeight ? fillRef : undefined}
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-card border border-line bg-surface shadow-card',
        className,
      )}
      style={fillHeight && height && !isMobile ? { height } : undefined}
    >
      {/* ── toolbar ─────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        {search ? (
          <div className="relative min-w-40 flex-1 sm:max-w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
            <input
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 w-full rounded-control border border-line bg-surface pl-8 pr-7 text-sm text-body placeholder:text-faint focus:border-accent focus:outline-none"
              aria-label={searchPlaceholder}
            />
            {searchDraft ? (
              <button
                type="button"
                onClick={() => setSearchDraft('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-faint hover:text-body"
                aria-label="Clear search"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>
        ) : (
          <div className="flex-1" />
        )}

        {toolbarActions}

        {activeFilterFields.length > 0 ? (
          <ToolbarButton
            active={!filtersOn}
            onClick={() => setFiltersOn((value) => !value)}
            title={filtersOn ? 'Bypass all filters' : 'Re-apply filters'}
          >
            {filtersOn ? <Filter className="size-3.5" /> : <FilterX className="size-3.5" />}
            <span className="hidden sm:inline">{filtersOn ? 'Filters on' : 'Filters off'}</span>
          </ToolbarButton>
        ) : null}

        <ToolbarButton
          ref={columnsAnchor}
          onClick={() => setColumnsOpen((value) => !value)}
          active={columnsOpen}
          title="Choose columns"
        >
          <Columns3 className="size-3.5" />
          <span className="hidden sm:inline">Columns</span>
        </ToolbarButton>

        {showsControl ? (
          <ToolbarButton
            onClick={() => (layout.autoSave ? update({ autoSave: false }) : saveNow())}
            active={unsaved}
            title={
              layout.autoSave
                ? 'Column layout saves automatically. Click to save manually instead.'
                : unsaved
                  ? 'Save this column layout'
                  : 'Layout saved. Click to save again.'
            }
          >
            <Save className="size-3.5" />
            <span className="hidden sm:inline">
              {layout.autoSave ? 'Auto-save' : unsaved ? 'Save layout' : 'Saved'}
            </span>
          </ToolbarButton>
        ) : null}

        <ToolbarButton
          onClick={() => void query.refetch()}
          title="Refresh"
          disabled={query.isFetching}
        >
          <RefreshCw className={cn('size-3.5', query.isFetching && 'animate-spin')} />
        </ToolbarButton>
      </div>

      {/* ── filter chips ────────────────────────────────── */}
      {activeFilterFields.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-line bg-surface-2/50 px-3 py-1.5">
          {activeFilterFields.map((field) => {
            const column = columns.find((c) => c.field === field);
            const value = filters[field];
            if (!value) return null;
            const off = disabledFilters.includes(field);
            return (
              <span
                key={field}
                className={cn(
                  'inline-flex items-center gap-1 rounded-pill border px-2 py-0.5 text-2xs',
                  off || !filtersOn
                    ? 'border-line bg-surface text-faint line-through'
                    : 'border-accent/25 bg-accent-subtle text-accent',
                )}
              >
                <button
                  type="button"
                  // A chip can be switched off without being thrown away, so a
                  // narrowing can be compared with and without one condition
                  // and then put back.
                  onClick={() =>
                    setDisabledFilters((previous) =>
                      previous.includes(field)
                        ? previous.filter((f) => f !== field)
                        : [...previous, field],
                    )
                  }
                  className="font-medium"
                  title={off ? 'Re-apply this filter' : 'Ignore this filter for now'}
                >
                  {column?.header ?? field}: {filterChipLabel(value)}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = { ...filters };
                    delete next[field];
                    setDisabledFilters((previous) => previous.filter((f) => f !== field));
                    setUrl({ filters: next });
                  }}
                  className="rounded-full p-0.5 hover:bg-black/5"
                  aria-label={`Remove the ${column?.header ?? field} filter`}
                >
                  <X className="size-2.5" />
                </button>
              </span>
            );
          })}
          <button
            type="button"
            onClick={clearAll}
            className="ml-1 text-2xs font-medium text-muted underline-offset-2 hover:text-danger hover:underline"
          >
            Clear all
          </button>
        </div>
      ) : null}

      {/* ── select-all-matching banner ──────────────────── */}
      {selectable && someOnPageSelected && !allMatching && total > pageKeys.length ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-line bg-accent-subtle px-3 py-1.5 text-2xs text-accent">
          <span>
            {formatNumber(selected.size)} selected on this page.
          </span>
          <button
            type="button"
            onClick={() => setAllMatching(true)}
            className="font-semibold underline underline-offset-2"
          >
            Select all {formatNumber(total)} matching
          </button>
        </div>
      ) : null}
      {allMatching ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-line bg-accent-subtle px-3 py-1.5 text-2xs text-accent">
          <span>
            All {formatNumber(total)} matching records are selected.
          </span>
          <button type="button" onClick={clearSelection} className="font-semibold underline underline-offset-2">
            Clear selection
          </button>
        </div>
      ) : null}

      {/* ── the table ───────────────────────────────────── */}
      {/* Desktop scrolls inside the table so the toolbar and pager stay put.
          Mobile lets the page scroll vertically and the table scroll only
          sideways — a nested vertical scroller on a phone traps the gesture —
          which is what an unbounded height gives it. */}
      {/* `isolate`: the sticky header and pinned columns order themselves
          here and never meet a page layer (tokens.css, layers). */}
      <ScrollArea className={cn('isolate', isMobile ? 'min-w-0' : 'min-h-0 flex-1')}>
        {body ?? (
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead className="sticky top-0 z-10">
              <tr>
                {selectable ? (
                  <th className="sticky left-0 z-20 w-10 border-b border-line bg-surface-2 px-3 py-2">
                    <Checkbox
                      checked={allOnPageSelected}
                      indeterminate={someOnPageSelected && !allOnPageSelected}
                      onChange={togglePage}
                      aria-label="Select all rows on this page"
                    />
                  </th>
                ) : null}
                {expandable ? (
                  <th className="w-8 border-b border-line bg-surface-2 px-1 py-2" aria-label="Expand" />
                ) : null}

                {displayed.map((column) => {
                  const direction = sortMapOf(sort)[column.field];
                  const sortIndex = sort.findIndex((c) => c.field === column.field);
                  const filterActive = isActiveFilter(filters[column.field]);
                  const pinned = layout.pins[column.field] === 'left';

                  return (
                    <th
                      key={column.field}
                      scope="col"
                      className={cn(
                        'group relative border-b border-line bg-surface-2 px-3 py-2 text-left font-medium text-muted whitespace-nowrap',
                        pinned && 'sticky left-0 z-20',
                        column.numeric && 'text-right',
                        column.align === 'center' && 'text-center',
                      )}
                      style={{
                        width: layout.widths[column.field] ?? column.width,
                        minWidth: column.minWidth ?? 80,
                      }}
                      aria-sort={
                        direction ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'
                      }
                    >
                      <div
                        className={cn(
                          'flex items-center gap-1',
                          column.numeric && 'justify-end',
                          column.align === 'center' && 'justify-center',
                        )}
                      >
                        {column.icon ? (
                          <span className="shrink-0 text-faint">{column.icon}</span>
                        ) : null}

                        {column.sortable !== false ? (
                          <button
                            type="button"
                            // Shift-click appends, which is how multi-sort is
                            // reached without a separate dialog.
                            onClick={(event) =>
                              setUrl({ sort: cycleSort(sort, column.field, event.shiftKey) })
                            }
                            className="inline-flex items-center gap-1 text-xs hover:text-body"
                            title="Click to sort. Shift-click to add to the sort."
                          >
                            <span className="truncate">{column.header}</span>
                            {direction === 'asc' ? (
                              <ArrowUp className="size-3 text-accent" />
                            ) : direction === 'desc' ? (
                              <ArrowDown className="size-3 text-accent" />
                            ) : (
                              <ArrowUp className="size-3 opacity-0 transition-opacity group-hover:opacity-40" />
                            )}
                            {/* The position is shown only for a multi-column
                                sort, where "which one wins" is a real question. */}
                            {sort.length > 1 && sortIndex >= 0 ? (
                              <span className="text-2xs text-accent tabular">{sortIndex + 1}</span>
                            ) : null}
                          </button>
                        ) : (
                          <span className="truncate text-xs">{column.header}</span>
                        )}

                        {column.filter ? (
                          <button
                            type="button"
                            ref={(node) => {
                              filterAnchors.current[column.field] = node;
                            }}
                            onClick={() =>
                              setOpenFilter((current) =>
                                current === column.field ? null : column.field,
                              )
                            }
                            className={cn(
                              'shrink-0 rounded p-0.5 transition-opacity',
                              filterActive
                                ? 'text-accent'
                                : 'text-faint opacity-0 group-hover:opacity-100 focus:opacity-100',
                            )}
                            aria-label={`Filter ${column.header}`}
                          >
                            <Filter className="size-3" />
                          </button>
                        ) : null}
                      </div>

                      {/* The resize handle. Wider than it looks so it is
                          grabbable without pixel-hunting. */}
                      <span
                        onPointerDown={(event) => startResize(column.field, event)}
                        className="absolute -right-1 top-0 h-full w-2 cursor-col-resize select-none"
                        aria-hidden
                      />
                    </th>
                  );
                })}

                {onView || onEdit || onDelete || rowActions ? (
                  <th className="w-10 border-b border-line bg-surface-2 px-2 py-2" aria-label="Actions" />
                ) : null}
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => {
                const key = rowKey(row);
                const isSelected = allMatching || selected.has(key);
                const isExpanded = expanded.has(key);
                const isRecent = recentKey === key;

                const menu: MenuItem[] = [
                  ...(onView ? [{ label: 'Open', onSelect: () => onView(row) }] : []),
                  ...(onEdit ? [{ label: 'Edit', onSelect: () => onEdit(row) }] : []),
                  ...(rowActions?.(row) ?? []),
                  ...(onDelete
                    ? [
                        {
                          label: 'Delete',
                          destructive: true,
                          separated: true,
                          onSelect: () => setPendingDelete(row),
                        },
                      ]
                    : []),
                ];

                return (
                  // Fragment rather than a wrapper element: a row and its
                  // detail row are siblings in <tbody>, and a <div> between
                  // them would break the table layout. It needs the key.
                  <Fragment key={key}>
                    <tr
                      ref={isRecent ? recentRef : undefined}
                      className={cn(
                        'transition-colors',
                        isSelected ? 'bg-accent-subtle/40' : 'hover:bg-surface-2/60',
                        isRecent && 'animate-row-flash',
                      )}
                    >
                      {selectable ? (
                        <td
                          className={cn(
                            'sticky left-0 z-10 border-b border-line/60 px-3 py-2',
                            isSelected ? 'bg-accent-subtle' : 'bg-surface',
                          )}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={allMatching}
                            onChange={() => toggleRow(key)}
                            aria-label="Select row"
                          />
                        </td>
                      ) : null}

                      {expandable ? (
                        <td className="border-b border-line/60 px-1 py-2">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(key)}
                            className="rounded p-0.5 text-faint hover:bg-surface-2 hover:text-body"
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                          >
                            {isExpanded ? (
                              <ChevronDown className="size-3.5" />
                            ) : (
                              <ChevronRight className="size-3.5" />
                            )}
                          </button>
                        </td>
                      ) : null}

                      {displayed.map((column) => {
                        const raw = (row as Record<string, unknown>)[column.field];
                        const pinned = layout.pins[column.field] === 'left';
                        const isEditing =
                          editing?.key === key && editing.field === column.field;

                        const content = (() => {
                          if (isEditing && onCellEdited) {
                            const setValue = (value: string) =>
                              setEditing((current) => (current ? { ...current, value } : current));
                            if (column.editRender) {
                              return column.editRender({
                                row,
                                value: editing.value,
                                setValue,
                                commit: (override) =>
                                  void commitEdit(row, column.field, override ?? editing.value),
                                cancel: () => setEditing(null),
                              });
                            }
                            return (
                              <input
                                autoFocus
                                value={editing.value}
                                onChange={(event) => setValue(event.target.value)}
                                onBlur={() => void commitEdit(row, column.field, editing.value)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    void commitEdit(row, column.field, editing.value);
                                  }
                                  // Escape abandons the edit. Without it the
                                  // only way out of a mistyped cell is to blur,
                                  // which saves it.
                                  if (event.key === 'Escape') setEditing(null);
                                }}
                                className="h-6 w-full rounded border border-accent bg-surface px-1 text-sm focus:outline-none"
                              />
                            );
                          }
                          if (column.render) return column.render(row);
                          if (column.format) return column.format(raw, row);
                          if (raw === null || raw === undefined || raw === '') {
                            return <span className="text-faint">—</span>;
                          }
                          return String(raw);
                        })();

                        const isLink = onView && column.field === linkField && !isEditing;

                        return (
                          <td
                            key={column.field}
                            onDoubleClick={
                              column.editable && onCellEdited
                                ? () =>
                                    setEditing({
                                      key,
                                      field: column.field,
                                      value: raw === null || raw === undefined ? '' : String(raw),
                                    })
                                : undefined
                            }
                            className={cn(
                              'border-b border-line/60 px-3 py-2 align-middle',
                              pinned && 'sticky left-0 z-10',
                              pinned && (isSelected ? 'bg-accent-subtle' : 'bg-surface'),
                              column.numeric && 'text-right tabular',
                              column.align === 'center' && 'text-center',
                              column.editable && onCellEdited && 'cursor-text',
                            )}
                            style={{ width: layout.widths[column.field] ?? column.width }}
                            title={column.editable && onCellEdited ? 'Double-click to edit' : undefined}
                          >
                            {isLink ? (
                              <button
                                type="button"
                                onClick={() => onView(row)}
                                className="truncate text-left font-medium text-accent hover:underline"
                              >
                                {content}
                              </button>
                            ) : (
                              <div className="truncate">{content}</div>
                            )}
                          </td>
                        );
                      })}

                      {menu.length > 0 ? (
                        <td className="border-b border-line/60 px-2 py-2 text-right">
                          <DropdownMenu
                            items={menu}
                            trigger={({ ref, onClick, ...aria }) => (
                              <button
                                ref={ref}
                                type="button"
                                onClick={onClick}
                                {...aria}
                                className="rounded p-1 text-faint hover:bg-surface-2 hover:text-body"
                                aria-label="Row actions"
                              >
                                <MoreHorizontal className="size-4" />
                              </button>
                            )}
                          />
                        </td>
                      ) : null}
                    </tr>

                    {isExpanded ? (
                      <tr className="bg-surface-2/40">
                        <td
                          colSpan={
                            displayed.length +
                            (selectable ? 1 : 0) +
                            (expandable ? 1 : 0) +
                            (menu.length > 0 ? 1 : 0)
                          }
                          className="border-b border-line px-4 py-3"
                        >
                          {renderRowDetail ? (
                            renderRowDetail(row)
                          ) : (
                            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                              {rowDetailFields?.(row).map((field) => (
                                <div key={field.label} className="min-w-0">
                                  <dt className="text-2xs uppercase tracking-wide text-faint">
                                    {field.label}
                                  </dt>
                                  {/* An absent value is an em dash rather than
                                      hidden, so the grid does not reflow
                                      between two rows. */}
                                  <dd className="truncate text-sm text-body">
                                    {field.value === null ||
                                    field.value === undefined ||
                                    field.value === ''
                                      ? '—'
                                      : field.value}
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </ScrollArea>

      {/* ── pager ───────────────────────────────────────── */}
      {!body || rows.length > 0 ? (
        <Pager
          page={page}
          pageSize={layout.pageSize}
          total={total}
          onPageChange={(next) => setUrl({ page: next })}
          onPageSizeChange={(size) => {
            update({ pageSize: size });
            setUrl({ page: 1 });
          }}
        />
      ) : null}

      {/* ── the bulk bar ────────────────────────────────── */}
      {selectable && selectionCount > 0 ? (
        <div className="pointer-events-none fixed bottom-5 left-1/2 z-page-bar -translate-x-1/2">
          <div className="pointer-events-auto flex animate-slide-up items-center gap-3 rounded-pill border border-line bg-surface px-4 py-2 shadow-raised">
            <Badge tone="accent">{formatNumber(selectionCount)} selected</Badge>
            {bulkActions?.({
              ids: Array.from(selected),
              allMatching,
              total,
              clear: clearSelection,
              request,
            })}
            {onBulkDelete ? (
              <Button
                variant="danger"
                size="sm"
                onClick={async () => {
                  await onBulkDelete(Array.from(selected), allMatching);
                  clearSelection();
                  await query.refetch();
                }}
              >
                <Trash2 className="size-3.5" />
                Delete
              </Button>
            ) : null}
            <button
              type="button"
              onClick={clearSelection}
              className="rounded p-1 text-faint hover:text-body"
              aria-label="Clear selection"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
      ) : null}

      {/* ── popovers ────────────────────────────────────── */}
      {openFilter
        ? (() => {
            const column = columns.find((c) => c.field === openFilter);
            if (!column?.filter) return null;
            const anchor = { current: filterAnchors.current[openFilter] ?? null };
            return (
              <Popover
                open
                onClose={() => setOpenFilter(null)}
                anchorRef={anchor}
                align="start"
                label={`Filter ${column.header}`}
              >
                <ColumnFilter
                  kind={column.filter}
                  header={column.header}
                  value={filters[openFilter]}
                  options={column.options}
                  facets={facetQuery}
                  onChange={(value) => {
                    const next = { ...filters };
                    if (value) next[openFilter] = value;
                    else delete next[openFilter];
                    setUrl({ filters: next });
                  }}
                  onClose={() => setOpenFilter(null)}
                />
              </Popover>
            );
          })()
        : null}

      <Popover
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        anchorRef={columnsAnchor}
        align="end"
        label="Columns"
      >
        <ColumnsPanel
          columns={ordered.map((column) => ({ field: column.field, header: column.header }))}
          hidden={hiddenFields}
          pins={layout.pins}
          onToggleVisible={(field) =>
            update({
              hidden: hiddenFields.includes(field)
                ? hiddenFields.filter((f) => f !== field)
                : [...hiddenFields, field],
            })
          }
          onMove={(field, toIndex) => {
            const fields = ordered.map((column) => column.field);
            const from = fields.indexOf(field);
            if (from < 0 || toIndex < 0 || toIndex >= fields.length) return;
            fields.splice(toIndex, 0, ...fields.splice(from, 1));
            update({ order: fields });
          }}
          onTogglePin={(field) => {
            const pins = { ...layout.pins };
            if (pins[field]) delete pins[field];
            else pins[field] = 'left';
            update({ pins });
          }}
          onReset={() => {
            reset();
            setColumnsOpen(false);
          }}
          onClose={() => setColumnsOpen(false)}
        />
      </Popover>

      {/* ── delete confirmation ─────────────────────────── */}
      {onDelete && pendingDelete ? (
        <Confirm
          open
          onClose={() => setPendingDelete(null)}
          onConfirm={async () => {
            await onDelete(pendingDelete);
            await query.refetch();
          }}
          title="Delete this record?"
          message={
            <>
              {deleteLabel
                ? `${deleteLabel(pendingDelete)} will be deleted.`
                : 'This record will be deleted.'}{' '}
              It will stop appearing in this register. Anything already referring
              to it keeps its reference.
            </>
          }
          confirmLabel="Delete"
        />
      ) : null}

      {/* Unsaved-arrangement reminder, shown only in manual mode. */}
      {showsControl && unsaved ? (
        <div className="flex items-center justify-between gap-2 border-t border-line bg-warning-subtle px-3 py-1.5 text-2xs text-warning">
          <span>This column layout is not saved.</span>
          <span className="flex gap-2">
            <button type="button" onClick={revert} className="underline underline-offset-2">
              Discard
            </button>
            <button type="button" onClick={saveNow} className="font-semibold underline underline-offset-2">
              Save
            </button>
          </span>
        </div>
      ) : null}
    </div>
  );
}
